import { inject, injectable, interfaces } from 'inversify';
import { ACDLError } from '../../../../../models/error.model';
import { IMainApiService } from '../../../../../services/api.service';
import { IApiService } from '../../../../../services/api.service.base';
import { ToolDeleteStrategy } from '../../../projectDetails.models';
import { IToolAffect, IToolUsage } from '../projectDetailsTools.models';

interface IAddAnnotationTypeRequest {
  projectId: string;
  annotationTypeId: string;
  name: string;
  color: string;
  order: number;
  selectorType: string;
}

interface IUpdateAnnotationTypeRequest {
  id: string;
  name: string;
  color: string;
  order: number;
  selectorType: string;
}

interface IUpdateAnnotationTypeOrderingRequest {
  projectId: string;
  toolOrders: { id: string; order: number }[];
}

interface IGetAnnotationToolUsageRequest {
  annotationTypeId: string;
}

interface IDeleteAnnotationTypeRequest {
  projectId: string;
  annotationTypeId: string;
  strategy: ToolDeleteStrategy;
}

interface ICheckAnnotationTypeNameUniquenessRequest {
  projectId: string;
  annotationTypeId: string;
  name: string;
}

interface IGetAnnotationToolAffectRequest {
  annotationTypeId: string;
}

export interface IAnnotationTypesApiService {
  addAnnotationTypeAsync(request: IAddAnnotationTypeRequest): Promise<void | ACDLError>;
  updateAnnotationTypeAsync(request: IUpdateAnnotationTypeRequest): Promise<void | ACDLError>;
  updateAnnotationTypesOrderAsync(request: IUpdateAnnotationTypeOrderingRequest): Promise<void | ACDLError>;
  getAnnotationTypeUsage(request: IGetAnnotationToolUsageRequest): Promise<IToolUsage | ACDLError>;
  deleteAnnotationTypeAsync(request: IDeleteAnnotationTypeRequest): Promise<void | ACDLError>;
  checkAnnotationTypeNameUniqueness(request: ICheckAnnotationTypeNameUniquenessRequest): Promise<boolean | ACDLError>;
  getAnnotationTypeAffect(request: IGetAnnotationToolAffectRequest): Promise<IToolAffect | ACDLError>;
}

export namespace IAnnotationTypesApiService {
  export const $: interfaces.ServiceIdentifier<IAnnotationTypesApiService> = Symbol('PROJECT_DETAILS_ANNOTATION_TYPES_API_SERVICE');
}

@injectable()
export class AnnotationTypesApiService implements IAnnotationTypesApiService {
  constructor(@inject(IMainApiService.$) private readonly apiService: IApiService) {}

  async addAnnotationTypeAsync(request: IAddAnnotationTypeRequest): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/AnnotationTypes/Add', request);
  }

  async updateAnnotationTypeAsync(request: IUpdateAnnotationTypeRequest): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/AnnotationTypes/Update', request);
  }

  async updateAnnotationTypesOrderAsync(request: IUpdateAnnotationTypeOrderingRequest): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/AnnotationTypes/UpdateAnnotationTypeOrdering', request);
  }

  async getAnnotationTypeUsage(request: IGetAnnotationToolUsageRequest): Promise<IToolUsage | ACDLError> {
    return await this.apiService.getAsync<IToolUsage>('/AnnotationTypes/GetUsage', { params: request });
  }

  async deleteAnnotationTypeAsync(request: IDeleteAnnotationTypeRequest): Promise<void | ACDLError> {
    return await this.apiService.postAsync('/AnnotationTypes/Delete', request);
  }

  async checkAnnotationTypeNameUniqueness(request: ICheckAnnotationTypeNameUniquenessRequest): Promise<boolean | ACDLError> {
    return await this.apiService.getAsync<boolean>('/AnnotationTypes/CheckAnnotationTypeNameUniqueness', { params: request });
  }

  async getAnnotationTypeAffect(request: IGetAnnotationToolAffectRequest): Promise<IToolAffect | ACDLError> {
    return await this.apiService.getAsync<IToolAffect>('/AnnotationTypes/GetAffect', { params: request });
  }
}
